import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ArrowLeft, Download, Pencil, Plus, Power, PowerOff, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/data-table/DataTable';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import { BulkEditDialog, type BulkEditField } from '@/components/data-table/BulkEditDialog';
import { FormSection } from '@/components/forms/Form';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormTextarea } from '@/components/forms/FormField';
import { FormCheckbox } from '@/components/forms/FormCheckbox';
import { useActivites } from '@/hooks/queries/useActivites';
import {
  useBulkDeleteServices,
  useBulkUpdateServiceActive,
  useDeleteService,
  useServices,
  useUpsertService,
  type UpsertServiceInput,
} from '@/hooks/queries/useServices';
import { useRole } from '@/hooks/useRole';
import { formatDA, formatPercent } from '@/lib/format';
import { t } from '@/locales/i18n';
import type { ServiceRow } from '@/lib/database.types';

const schema = z.object({
  code: z.string().min(1),
  nom: z.string().min(2),
  description: z.string().optional().nullable(),
  prix_ht: z.coerce.number().min(0),
  tva_pct: z.coerce.number().min(0).max(100),
  unite: z.string().min(1),
  is_active: z.boolean(),
});

type ServiceForm = z.infer<typeof schema>;

export function ServicesPage(): JSX.Element {
  const { entrepriseId = '', activiteId = '' } = useParams<{
    entrepriseId: string;
    activiteId: string;
  }>();
  const { isAdmin } = useRole();
  const { data: activites = [] } = useActivites(entrepriseId);
  const activite = activites.find((a) => a.id === activiteId);
  const { data: services = [], isLoading } = useServices(activiteId);
  const upsert = useUpsertService();
  const remove = useDeleteService();
  const bulkDelete = useBulkDeleteServices();
  const bulkActive = useBulkUpdateServiceActive();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ServiceRow | null>(null);

  const [selectedRows, setSelectedRows] = useState<ServiceRow[]>([]);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const clearSelection = (): void => setSelectionVersion((v) => v + 1);

  const columns = useMemo<ColumnDef<ServiceRow>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Code',
        size: 110,
        cell: ({ getValue }) => <span className="font-mono text-xs">{String(getValue())}</span>,
      },
      { accessorKey: 'nom', header: 'Intitulé' },
      {
        accessorKey: 'prix_ht',
        header: 'Prix HT',
        size: 130,
        cell: ({ getValue }) => <span className="num text-xs">{formatDA(Number(getValue()))}</span>,
      },
      {
        accessorKey: 'tva_pct',
        header: 'TVA',
        size: 80,
        cell: ({ getValue }) => (
          <span className="num text-xs">{formatPercent(Number(getValue()) / 100, 1)}</span>
        ),
      },
      {
        accessorKey: 'unite',
        header: 'Unité',
        size: 110,
        cell: ({ getValue }) => (
          <span className="text-xs text-muted-foreground">{String(getValue())}</span>
        ),
      },
      {
        accessorKey: 'is_active',
        header: 'Statut',
        size: 90,
        cell: ({ getValue }) =>
          getValue() ? (
            <Badge variant="success">{t('common.active')}</Badge>
          ) : (
            <Badge variant="outline">{t('common.inactive')}</Badge>
          ),
      },
      {
        id: 'actions',
        header: '',
        size: 90,
        enableSorting: false,
        cell: ({ row }) =>
          isAdmin ? (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => {
                  setEditing(row.original);
                  setDialogOpen(true);
                }}
                aria-label="Modifier"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => setConfirmDelete(row.original)}
                aria-label="Supprimer"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : null,
      },
    ],
    [isAdmin],
  );

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={`${t('parametres.organisation.services.title')}${activite ? ' — ' + activite.nom : ''}`}
        subtitle="Catalogue de services facturables pour l'activité."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline">
              <Link to={`/parametres/organisation/${entrepriseId}/activites`}>
                <ArrowLeft className="h-4 w-4" />
                {t('common.back')}
              </Link>
            </Button>
            {isAdmin && (
              <Button
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                {t('parametres.organisation.services.addService')}
              </Button>
            )}
          </div>
        }
      />

      <div className="p-6">
        <DataTable
          key={selectionVersion}
          data={services}
          columns={columns}
          loading={isLoading}
          pageSize={20}
          searchPlaceholder="Rechercher un service…"
          exportFileName="services.csv"
          enableSelection={isAdmin}
          getRowId={(row) => row.id}
          onSelectionChange={setSelectedRows}
          emptyMessage={
            'Aucun service. Cliquez sur « Ajouter un service » pour démarrer le catalogue.'
          }
          bulkBar={
            <BulkActionBar count={selectedRows.length} onClear={clearSelection}>
              <BulkButton onClick={() => exportServicesCSV(selectedRows)}>
                <Download className="h-3.5 w-3.5" /> Exporter CSV
              </BulkButton>
              <BulkButton
                onClick={async () => {
                  await bulkActive.mutateAsync({
                    ids: selectedRows.map((s) => s.id),
                    is_active: true,
                  });
                  toast.success(`${selectedRows.length} service(s) activé(s)`);
                  clearSelection();
                }}
              >
                <Power className="h-3.5 w-3.5" /> Activer
              </BulkButton>
              <BulkButton
                onClick={async () => {
                  await bulkActive.mutateAsync({
                    ids: selectedRows.map((s) => s.id),
                    is_active: false,
                  });
                  toast.success(`${selectedRows.length} service(s) désactivé(s)`);
                  clearSelection();
                }}
              >
                <PowerOff className="h-3.5 w-3.5" /> Désactiver
              </BulkButton>
              <BulkButton onClick={() => setBulkEditOpen(true)}>
                <Pencil className="h-3.5 w-3.5" /> Modifier en masse
              </BulkButton>
              <BulkButton variant="destructive" onClick={() => setBulkConfirmDelete(true)}>
                <Trash2 className="h-3.5 w-3.5" /> Supprimer
              </BulkButton>
            </BulkActionBar>
          }
        />
      </div>

      <ServiceFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSubmit={async (values) => {
          await upsert.mutateAsync({
            id: editing?.id,
            activite_id: activiteId,
            ...values,
          } as UpsertServiceInput);
          toast.success(editing ? 'Service mis à jour' : 'Service créé');
          setDialogOpen(false);
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer le service « ${confirmDelete?.nom} » ?`}
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await remove.mutateAsync(confirmDelete.id);
          toast.success('Service supprimé');
          setConfirmDelete(null);
        }}
      />

      <BulkEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        table="services"
        ids={selectedRows.map((r) => r.id)}
        fields={
          [
            { key: 'prix_ht', label: 'Prix HT (DA)', type: 'number' },
            { key: 'tva_pct', label: 'TVA %', type: 'number' },
            {
              key: 'is_active',
              label: 'Statut',
              type: 'select',
              options: [
                { value: 'true', label: 'Actif' },
                { value: 'false', label: 'Inactif' },
              ],
            },
          ] satisfies BulkEditField[]
        }
        onApplied={() => clearSelection()}
      />

      <DangerousConfirmDialog
        open={bulkConfirmDelete}
        onOpenChange={setBulkConfirmDelete}
        title={`Supprimer ${selectedRows.length} service${selectedRows.length > 1 ? 's' : ''} ?`}
        description="Action irréversible. Les opérations historiques liées garderont service_id à NULL."
        consequences={[
          `${selectedRows.length} ligne(s) seront définitivement effacées`,
          'Les opérations existantes garderont leur référence (set null)',
          "L'action est journalisée dans audit_log",
        ]}
        challenge="SUPPRIMER"
        loading={bulkDelete.isPending}
        onConfirm={async () => {
          await bulkDelete.mutateAsync(selectedRows.map((s) => s.id));
          toast.success(`${selectedRows.length} service(s) supprimé(s)`);
          setBulkConfirmDelete(false);
          clearSelection();
        }}
      />
    </div>
  );
}

function exportServicesCSV(rows: ServiceRow[]): void {
  if (rows.length === 0) return;
  const headers = ['code', 'nom', 'prix_ht', 'tva_pct', 'unite', 'is_active', 'description'];
  const csv = [
    headers.join(','),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const v = (r as unknown as Record<string, unknown>)[h];
          const s = v == null ? '' : String(v);
          return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `services-${rows.length}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`${rows.length} service(s) exporté(s)`);
}

function ServiceFormDialog({
  open,
  onOpenChange,
  editing,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: ServiceRow | null;
  onSubmit: (values: ServiceForm) => Promise<void>;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ServiceForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: '',
      nom: '',
      prix_ht: 0,
      tva_pct: 19,
      unite: 'forfait',
      is_active: true,
    },
  });

  useEffect(() => {
    if (open) {
      reset(
        editing
          ? {
              code: editing.code,
              nom: editing.nom,
              description: editing.description,
              prix_ht: editing.prix_ht,
              tva_pct: editing.tva_pct,
              unite: editing.unite,
              is_active: editing.is_active,
            }
          : { code: '', nom: '', prix_ht: 0, tva_pct: 19, unite: 'forfait', is_active: true },
      );
    }
  }, [open, editing, reset]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={
        editing
          ? t('parametres.organisation.services.editService')
          : t('parametres.organisation.services.addService')
      }
      description="Code unique par activité."
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(async (v) => {
        await onSubmit(v);
      })}
    >
      <FormSection>
        <FormField label="Code" required {...register('code')} error={errors.code?.message} />
        <FormField
          label="Unité"
          required
          {...register('unite')}
          hint="forfait, heure, jour, participant…"
        />
        <FormField
          label="Intitulé"
          required
          {...register('nom')}
          error={errors.nom?.message}
          fullWidth
        />
        <FormField
          type="number"
          step="0.01"
          label="Prix HT (DA)"
          required
          {...register('prix_ht')}
        />
        <FormField type="number" step="0.01" label="TVA %" required {...register('tva_pct')} />
        <FormCheckbox label="Service actif" {...register('is_active')} fullWidth />
        <FormTextarea label="Description" {...register('description')} fullWidth />
      </FormSection>
    </FormDialog>
  );
}
